import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useWebsite } from 'providers/WebsiteProvider';

export const AuthContext = createContext(null);

export const PENDING_SHOP_CREDIT_STORAGE_KEY = 'pendingShopCredit';
export const SHOP_CREDIT_SYNC_DELAY = 60 * 1000;

const readPendingShopCredit = () => {
	try {
		const value = JSON.parse(localStorage.getItem(PENDING_SHOP_CREDIT_STORAGE_KEY));
		return value
			&& typeof value.authToken === 'string'
			&& Number.isFinite(Number(value.expectedCredit))
			&& Number.isFinite(Number(value.refreshAt))
			? value
			: null;
	} catch {
		return null;
	}
};

export const useAuth = () => {
	return useContext(AuthContext);
};

export default function AuthProvider(props) {
	const [authChecked, setAuthChecked] = useState(false);
	const [token, setToken] = useState(null);
	const [userData, setUserData] = useState(null);
	const pendingCreditRefreshTimerRef = useRef(null);
	const pendingShopCreditRef = useRef(readPendingShopCredit());
	const { setProfileOpen } = useWebsite();
	const navigate = useNavigate();

	const clearPendingShopCredit = useCallback(() => {
		if (pendingCreditRefreshTimerRef.current) {
			clearTimeout(pendingCreditRefreshTimerRef.current);
			pendingCreditRefreshTimerRef.current = null;
		}

		pendingShopCreditRef.current = null;
		try {
			localStorage.removeItem(PENDING_SHOP_CREDIT_STORAGE_KEY);
		} catch (error) {
			console.error('Pending shop credit cleanup failed:', error);
		}
	}, []);

	const logoutBySessionExpired = useCallback((cb) => {
		console.log('SESSION EXPIRED');
		clearPendingShopCredit();
		setToken(null);
		localStorage.removeItem('FD_TOKEN');
		setUserData(null);
		setProfileOpen(false);
		setAuthChecked(true);
		cb?.();
		navigate('/bejelentkezes');
	}, [clearPendingShopCredit, navigate, setProfileOpen]);

	const extendUserData = useCallback(async (baseUserData, cb, preservePendingCredit = true) => {
		console.log('Extend user data.');
		const url = global.backingsEndpoint + 'employeeUserControl/getUserVIPDataFromHUMUSZ';
		const response = await axios.post(url);
		const vipData = response.data && typeof response.data === 'object' ? response.data : {};
		const refreshedUserData = { ...vipData, ...baseUserData };
		if (vipData.credit !== null && vipData.credit !== undefined) {
			refreshedUserData.credit = vipData.credit;
		}
		const pendingCredit = pendingShopCreditRef.current || readPendingShopCredit();

		if (pendingCredit?.authToken === token) {
			const expectedCredit = Number(pendingCredit.expectedCredit);
			const hasBackendCredit = refreshedUserData.credit !== null
				&& refreshedUserData.credit !== undefined
				&& Number.isFinite(Number(refreshedUserData.credit));
			const backendCredit = Number(refreshedUserData.credit);
			const isPending = preservePendingCredit
				&& Date.now() < Number(pendingCredit.refreshAt)
				&& (!hasBackendCredit || backendCredit > expectedCredit);

			if (isPending) {
				refreshedUserData.credit = expectedCredit;
			} else {
				clearPendingShopCredit();
			}
		}

		setUserData(refreshedUserData);
		console.log('Done.');
		cb?.(refreshedUserData);
		return refreshedUserData;
	}, [clearPendingShopCredit, token]);

	const getUserData = useCallback(async (cb, options = {}) => {
		if (!token) return null;

		const url = global.backingsEndpoint + 'accessManagement/getLoggedInClientDto';

		try {
			const response = await axios.post(url);

			if (response.data && Object.keys(response.data).length > 0) {
				return await extendUserData(
					response.data,
					cb,
					options.preservePendingCredit !== false
				);
			}

			logoutBySessionExpired(cb);
			return null;
		} catch (error) {
			console.error('Get user data error:', error);
			logoutBySessionExpired(cb);
			return null;
		}
	}, [extendUserData, logoutBySessionExpired, token]);

	const schedulePendingCreditRefresh = useCallback((pendingCredit) => {
		if (!pendingCredit || pendingCredit.authToken !== token) return;

		if (pendingCreditRefreshTimerRef.current) {
			clearTimeout(pendingCreditRefreshTimerRef.current);
		}

		const delay = Math.max(0, Number(pendingCredit.refreshAt) - Date.now());
		pendingCreditRefreshTimerRef.current = setTimeout(async () => {
			pendingCreditRefreshTimerRef.current = null;
			await getUserData(undefined, { preservePendingCredit: false });
		}, delay);
	}, [getUserData, token]);

	// Check for stored token on mount
	useEffect(() => {
		const storedToken = localStorage.getItem('FD_TOKEN');
		if (storedToken) {
			console.log('Stored token found:', storedToken);
			setToken(storedToken);
		} else {
			console.log('No token stored');
			setAuthChecked(true);
		}
	}, []);

	// Handle token changes
	useEffect(() => {
		console.log('Token changed:', token);
		if (token) {
			localStorage.setItem('FD_TOKEN', token);
			console.log('Checking session...');
			getUserData();

			const pendingCredit = readPendingShopCredit();
			if (pendingCredit?.authToken === token) {
				pendingShopCreditRef.current = pendingCredit;
				schedulePendingCreditRefresh(pendingCredit);
			} else if (pendingCredit) {
				clearPendingShopCredit();
			}
		} else {
			localStorage.removeItem('FD_TOKEN');
			setUserData(null);
		}
	}, [clearPendingShopCredit, getUserData, schedulePendingCreditRefresh, token]);

	useEffect(() => () => {
		if (pendingCreditRefreshTimerRef.current) {
			clearTimeout(pendingCreditRefreshTimerRef.current);
		}
	}, []);

	useEffect(() => {
		setAuthChecked(true);
	}, [userData]);

	const login = (email, password, onStart, onError) => {
		if (onStart) onStart();

		const url = global.backingsEndpoint + 'userLogin/loginToken';
		const body = { email, password };

		return axios.post(url, body)
			.then(response => {
				if (response.data) {
					setToken(response.data);
				} else if (onError) {
					onError();
				}
			})
			.catch(error => {
				console.error('Login error:', error);
				if (onError) onError();
			});
	};

	const logout = () => {
		clearPendingShopCredit();
		setToken(null);
		localStorage.removeItem('FD_TOKEN');
		setUserData(null);
		setProfileOpen(false);
		setAuthChecked(true);
		navigate('/');
	};

	function refreshUserData(cb) {
		return getUserData(cb);
	};

	function redeemUserCredit(redeemedPoints) {
		const currentCredit = Number(userData?.credit);
		const points = Number(redeemedPoints);

		if (!token || !Number.isFinite(currentCredit) || !Number.isFinite(points) || points <= 0) {
			return currentCredit;
		}

		const expectedCredit = Math.max(0, currentCredit - points);
		const pendingCredit = {
			authToken: token,
			expectedCredit,
			refreshAt: Date.now() + SHOP_CREDIT_SYNC_DELAY,
		};

		pendingShopCreditRef.current = pendingCredit;
		try {
			localStorage.setItem(PENDING_SHOP_CREDIT_STORAGE_KEY, JSON.stringify(pendingCredit));
		} catch (error) {
			console.error('Pending shop credit persistence failed:', error);
		}
		setUserData((currentUserData) => currentUserData
			? { ...currentUserData, credit: expectedCredit }
			: currentUserData
		);
		schedulePendingCreditRefresh(pendingCredit);

		return expectedCredit;
	};

	function modifyUserDataValue(key, value) {
		setUserData((currentUserData) => currentUserData
			? { ...currentUserData, [key]: value }
			: currentUserData
		);
	};

	const value = {
		authChecked,
		token,
		userData,
		login,
		logout,
		getUserData,
		refreshUserData,
		redeemUserCredit,
		modifyUserDataValue
	};

	return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}
